import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchUpcomingCalendarEvents,
  fetchWasteTypes,
  fetchSpecialEvents,
  cachedFetch,
} from "../api";
import NavigationMenu from "../components/NavigationMenu";
import PageHeader from "../components/PageHeader";
import PageLoader from "../components/PageLoader";
import WasteDetailSheet from "../components/WasteDetailSheet";
import WasteIcon, { resolveColor } from "../components/WasteIcon";
import { ChevronRight, Calendar as CalendarIcon } from "lucide-react";
import styles from "./HomePage.module.scss";
import ErrorState from "../components/ErrorState";
import { useRetry } from "../hooks/useRetry";
import { CalendarEvent, WasteType, SpecialEvent } from "../types";
import { localized } from "../utils/localized";
import { getRelativeLabel, isUpcoming } from "../utils/relativeDate";
import { useLang } from "../hooks/useLang";
import { useDocumentTitle } from "../hooks/useDocumentTitle";

/**
 * Render a relative label ("Today", "Tomorrow", "Monday", "In 12 days").
 * The date arithmetic lives in utils/relativeDate so it can be tested.
 */
const formatRelative = (
  date: Date,
  t: (key: string, opts?: any) => string,
  locale: string,
): string => {
  const label = getRelativeLabel(date);

  switch (label.kind) {
    case "today":
      return t("today");
    case "tomorrow":
      return t("tomorrow");
    case "weekday":
      return date.toLocaleDateString(locale, { weekday: "long" });
    default:
      return t("in_days", { count: label.days });
  }
};

const HomePage: React.FC = () => {
  const [nextEvent, setNextEvent] = useState<CalendarEvent | null>(null);
  const [upcomingEvents, setUpcomingEvents] = useState<CalendarEvent[]>([]);
  const [specialEvents, setSpecialEvents] = useState<SpecialEvent[]>([]);
  const [wasteTypes, setWasteTypes] = useState<Record<string, WasteType>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { retryKey, retry } = useRetry();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [selectedWasteType, setSelectedWasteType] = useState<WasteType | null>(
    null,
  );
  const { t, i18n } = useTranslation();
  useDocumentTitle();

  useEffect(() => {
    const loadData = async () => {
      // Reset on every run so a retry clears the previous failure.
      setLoading(true);
      setError(null);

      try {
        const now = new Date();
        const fromDate = now.toISOString().slice(0, 10);
        const isForce = retryKey > 0;
        const [calendarData, wasteTypesData, specialEventsData] =
          await Promise.all([
            cachedFetch(`upcoming-${fromDate}`, () => fetchUpcomingCalendarEvents(fromDate), { force: isForce }),
            cachedFetch("waste-types", fetchWasteTypes, { force: isForce }),
            cachedFetch("special-events", fetchSpecialEvents, { force: isForce }),
          ]);

        // Transform wasteTypesData into a map for easy lookup
        const wasteTypesMap = wasteTypesData.reduce(
          (acc: Record<string, WasteType>, type: WasteType) => {
            acc[type.waste_id] = type;
            return acc;
          },
          {},
        );
        setWasteTypes(wasteTypesMap);

        // Filter and sort special events for upcoming ones
        const upcomingSpecialEvents = (specialEventsData || [])
          .filter((event: SpecialEvent) => {
            if (event.recurring) return true;
            if (event.date) return new Date(event.date) >= now;
            if (event.dates && event.dates.length > 0) {
              return event.dates.some((d: string) => new Date(d) >= now);
            }
            return false;
          })
          .slice(0, 3);
        setSpecialEvents(upcomingSpecialEvents);

        const events = calendarData.map((event: any) => ({
          ...event,
          event_date: new Date(event.event_date),
        }));

        // Today's collection stops being actionable once the trucks
        // have been round; later days always count as upcoming.
        const futureEvents = events
          .filter((event: CalendarEvent) =>
            isUpcoming(new Date(event.event_date), now),
          )
          .sort(
            (a: CalendarEvent, b: CalendarEvent) =>
              (a.event_date as Date).getTime() -
              (b.event_date as Date).getTime(),
          );

        if (futureEvents.length > 0) {
          setNextEvent(futureEvents[0]);
          setUpcomingEvents(futureEvents.slice(1, 6)); // Next 5 events
        } else {
          setNextEvent(null);
          setUpcomingEvents([]);
        }
      } catch (err) {
        // The message on the exception is for the console, not for a
        // resident: it reads "Request failed (HTTP 500)".
        setError(t("failed_to_load_data"));
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [t, retryKey]);

  const formatDate = (date: Date | string) => {
    const d = new Date(date);
    return d
      .toLocaleDateString(i18n.language, {
        weekday: "short",
        day: "2-digit",
        month: "short",
      })
      .toUpperCase();
  };

  const handleEventClick = (event: CalendarEvent) => {
    const wasteType = wasteTypes[event.waste_id];
    if (wasteType) {
      setSelectedWasteType(wasteType);
    }
  };

  const handleCloseModal = () => {
    setSelectedWasteType(null);
  };

  const lang = useLang();

  if (loading) return <PageLoader />;
  if (error) return <ErrorState message={error} onRetry={retry} />;

  return (
    <div className={styles.homePage}>
      <NavigationMenu
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
      />

      <PageHeader title="Sortsofort" onMenuClick={() => setIsMenuOpen(true)} />

      <main className={styles.mainContent}>
        {/* Hero Section: Next Collection */}
        <section className={styles.heroSection}>
          <h2 className={styles.sectionTitle}>{t("next_collection")}</h2>
          {nextEvent ? (
            <div className={styles.heroCard}>
              <div className={styles.heroContent}>
                <p className={styles.heroDate}>
                  {formatRelative(
                    nextEvent.event_date as Date,
                    t,
                    i18n.language,
                  ).toUpperCase()}
                </p>
                <p className={styles.heroWasteType}>
                  {localized(wasteTypes[nextEvent.waste_id], "name", lang) ||
                    t("unknown_waste")}
                </p>
                <div className={styles.heroBadge}>
                  {formatDate(nextEvent.event_date)}
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.emptyState}>
              {t("no_upcoming_collections")}
            </div>
          )}
        </section>

        {/* Upcoming List */}
        <section>
          <h2 className={styles.sectionTitle}>{t("upcoming")}</h2>
          <div className={styles.eventList}>
            {upcomingEvents.length > 0 ? (
              upcomingEvents.map((event, index) => {
                const wasteType = wasteTypes[event.waste_id];
                const color = resolveColor(wasteType, event.waste_id);
                return (
                  <button
                    key={event.event_id}
                    className={styles.eventCard}
                    style={{ animationDelay: `${0.1 * (index + 1)}s` }}
                    onClick={() => handleEventClick(event)}
                    aria-label={`${localized(wasteType, "name", lang) || t("unknown_waste")} — ${formatDate(event.event_date)}`}
                  >
                    <div
                      className={styles.eventIcon}
                      style={{
                        background: `linear-gradient(135deg, ${color}33 0%, ${color}1a 100%)`,
                      }}
                    >
                      <WasteIcon
                        wasteId={event.waste_id}
                        wasteType={wasteType}
                        size={22}
                        strokeWidth={2}
                      />
                    </div>
                    <div className={styles.eventContent}>
                      <p className={styles.eventName}>
                        {localized(wasteType, "name", lang) ||
                          t("unknown_waste")}
                      </p>
                      <p className={styles.eventDate}>
                        {formatDate(event.event_date)}
                      </p>
                    </div>
                    <ChevronRight
                      className={styles.eventChevron}
                      size={20}
                      aria-hidden="true"
                    />
                  </button>
                );
              })
            ) : (
              <p className={styles.emptyState}>{t("no_more_upcoming")}</p>
            )}
          </div>
        </section>

        {/* Special Events Section */}
        {specialEvents.length > 0 && (
          <section className={styles.specialEventsSection}>
            <h2 className={styles.sectionTitle}>{t("special_events")}</h2>
            <div className={styles.eventList}>
              {specialEvents.map((event, index) => {
                const eventName = localized(event, "name", lang);

                let displayDate = "";
                if (event.date) {
                  displayDate = new Date(event.date).toLocaleDateString(
                    i18n.language,
                    {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    },
                  );
                } else if (event.dates && event.dates.length > 0) {
                  const now = new Date();
                  const nextDate = event.dates.find(
                    (d: string) => new Date(d) >= now,
                  );
                  if (nextDate) {
                    displayDate = new Date(nextDate).toLocaleDateString(
                      i18n.language,
                      {
                        day: "numeric",
                        month: "short",
                      },
                    );
                  }
                } else if (event.recurring) {
                  displayDate = t("monthly");
                }

                return (
                  <div
                    key={event.event_id}
                    className={styles.eventCard}
                    style={{ animationDelay: `${0.1 * (index + 1)}s` }}
                  >
                    <div className={styles.eventIcon}>
                      <CalendarIcon size={20} aria-hidden="true" />
                    </div>
                    <div className={styles.eventContent}>
                      <p className={styles.eventName}>{eventName}</p>
                      <p className={styles.eventDate}>
                        {displayDate.toUpperCase()}
                      </p>
                      {event.time && (
                        <p className={styles.eventTime}>{event.time}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </main>

      {/* Waste Detail Bottom-Sheet Modal */}
      <WasteDetailSheet
        wasteType={selectedWasteType}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default HomePage;
